<template>
  <v-container>
    <v-row class="mb-4">
      <v-col cols="12">
        <h1>Employee Comparison</h1>
      </v-col>
    </v-row>

    <v-card class="mb-6">
      <v-card-title>Comparison Filters</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.dateOfJoining"
                  label="Date of Joining"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.dateOfJoining"
                @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="filters.grade"
              :items="gradeOptions"
              label="Grade"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="filters.highestQualification"
              :items="qualificationOptions"
              label="Highest Qualification"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.additionalQualification"
              label="Additional Qualification"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.certificationCourses"
              label="Certification Courses"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.workExperience"
              label="Work Experience (years)"
              type="number"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.joiningSalary"
              label="Joining Salary"
              type="number"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.currentSalary"
              label="Current Salary"
              type="number"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="compareEmployees">Compare</v-btn>
      </v-card-actions>
    </v-card>

    <v-row v-if="selectedEmployees && selectedEmployees.length > 0 && !selectAll" class="mb-6" style="background-color: #f5f5f5; border-radius: 8px; padding: 10px;">
      <v-col cols="12">
        <h2 class="mb-2" style="color: #1976D2">Selected Employees ({{ selectedEmployees.length }})</h2>
        <v-row>
          <v-col v-for="employee in selectedEmployees" :key="'selected-' + (employee.id || employee._id)" cols="12" sm="6" md="4">
            <v-card elevation="8" style="border: 2px solid #1976D2">
              <v-card-title>
                <v-checkbox
                  :input-value="selectedEmployeeIds.includes((employee.id || employee._id || '').toString())"
                  @change="val => toggleSelection((employee.id || employee._id || '').toString(), val)"
                  hide-details
                  class="mt-0 mr-2"
                ></v-checkbox>
                {{ employee.name }}
              </v-card-title>
              <v-card-text>
                <v-list dense>
                  <template v-for="(value, key) in employee">
                    <v-list-item :key="key" v-if="key !== 'id' && key !== '_id' && key !== 'name'">
                      <v-list-item-content>
                        <v-list-item-title>{{ formatKey(key) }}</v-list-item-title>
                        <v-list-item-subtitle>{{ value }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row v-if="comparisonResults.length" class="mb-2">
      <v-col cols="12">
        <v-checkbox
          v-model="allSelected"
          label="Select All"
        ></v-checkbox>
      </v-col>
    </v-row>

    <v-row v-if="comparisonResults.length">
      <v-col v-for="employee in comparisonResults" :key="employee.id || employee._id" cols="12" sm="6" md="4">
        <v-card :elevation="selectedEmployeeIds.includes((employee.id || employee._id || '').toString()) ? 8 : 2" :style="selectedEmployeeIds.includes((employee.id || employee._id || '').toString()) ? 'border: 2px solid #1976D2' : ''">
          <v-card-title>
            <v-checkbox
              :input-value="selectedEmployeeIds.includes((employee.id || employee._id || '').toString())"
              @change="val => toggleSelection((employee.id || employee._id || '').toString(), val)"
              hide-details
              class="mt-0 mr-2"
            ></v-checkbox>
            {{ employee.name }}
          </v-card-title>
          <v-card-text>
            <v-list dense>
              <template v-for="(value, key) in employee">
                <v-list-item :key="key" v-if="key !== 'id' && key !== '_id' && key !== 'name'">
                  <v-list-item-content>
                    <v-list-item-title>{{ formatKey(key) }}</v-list-item-title>
                    <v-list-item-subtitle>{{ value }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'EmployeeComparison',
  data() {
    return {
      dateMenu: false,
      filters: {
        dateOfJoining: null,
        grade: null,
        highestQualification: null,
        additionalQualification: null,
        certificationCourses: null,
        workExperience: null,
        joiningSalary: null,
        currentSalary: null,
      },
      gradeOptions: ['Grade A', 'Grade B', 'Grade C'],
      qualificationOptions: ['Bachelor', 'Master', 'PhD'],
      comparisonResults: [],
      selectedEmployeeIds: [],
      selectedEmployees: [],
    };
  },
  computed: {
    selectAll() {
      if (!this.comparisonResults || this.comparisonResults.length === 0) {
        return false;
      }
      return this.selectedEmployeeIds.length === this.comparisonResults.length;
    },
    allSelected: {
      get() {
        return this.selectAll;
      },
      set(val) {
        this.toggleSelectAll(val);
      }
    }
  },
  watch: {
    comparisonResults() {
      this.selectedEmployeeIds = [];
      this.selectedEmployees = [];
    }
  },
  methods: {
    compareEmployees() {
      axios.post('/api/compare-employees', this.filters, {
        headers: {
          'x-auth-token': localStorage.getItem('token'),
        },
      })
        .then(response => {
          this.comparisonResults = response.data;
        })
        .catch(error => {
          console.error('Error comparing employees:', error);
        });
    },
    formatKey(key) {
      return key.split(/(?=[A-Z])/).join(' ').replace(/^\w/, c => c.toUpperCase());
    },
    toggleSelectAll(val) {
      if (val) {
        this.selectedEmployeeIds = this.comparisonResults.map(
          e => (e.id || e._id || '').toString()
        );
        this.selectedEmployees = [];
      } else {
        this.selectedEmployeeIds = [];
        this.selectedEmployees = [];
      }
    },
    toggleSelection(id, isSelected) {
      if (isSelected) {
        if (!this.selectedEmployeeIds.includes(id)) {
          this.selectedEmployeeIds.push(id);
          const item = this.comparisonResults.find(e => (e.id || e._id || '').toString() === id);
          if (item) this.selectedEmployees.push(item);
        }
      } else {
        this.selectedEmployeeIds = this.selectedEmployeeIds.filter(x => x !== id);
        this.selectedEmployees = this.selectedEmployees.filter(e => (e.id || e._id || '').toString() !== id);
      }
      this.selectedEmployeeIds = [...this.selectedEmployeeIds];
    },
  },
};
</script>